import type { AxiosInstance } from "axios";

/**
 * The store's ad-account connections, as the settings screen sees them.
 *
 * These mirror the API's `integrations` module rather than importing it — apps/web cannot
 * pull server code into a browser bundle, the same trade `lib/reports.ts` makes.
 */
export type Platform = "meta" | "google";

export interface IntegrationAccount {
  readonly id: string;
  readonly name: string | null;
  readonly selected: boolean;
}

export interface IntegrationSummary {
  readonly platform: Platform;
  readonly connected: boolean;
  readonly account: string | null;
  readonly email: string | null;
  readonly accounts: readonly IntegrationAccount[];
}

export const PLATFORM_LABEL: Readonly<Record<Platform, string>> = { meta: "Meta", google: "Google" };

export async function fetchIntegrations(client: AxiosInstance): Promise<readonly IntegrationSummary[]> {
  const response = await client.get<{ success: boolean; data: readonly IntegrationSummary[] }>("/api/v2/integrations");
  return response.data.data;
}

/**
 * Start a connect. The API returns a provider URL; the browser must NAVIGATE to it, not
 * fetch it — an OAuth consent page is a full-page redirect the user interacts with, and
 * XHR-ing it would just download HTML. `window.location.assign` is the whole point.
 */
export async function beginConnect(client: AxiosInstance, platform: Platform): Promise<string> {
  const response = await client.post<{ success: boolean; data: { redirectUrl: string } }>(
    `/api/v2/integrations/${platform}/connect`,
  );
  return response.data.data.redirectUrl;
}

export async function chooseAccounts(
  client: AxiosInstance,
  platform: Platform,
  selected: readonly string[],
): Promise<readonly IntegrationSummary[]> {
  const response = await client.post<{ success: boolean; data: readonly IntegrationSummary[] }>(
    `/api/v2/integrations/${platform}/accounts`,
    { selected },
  );
  return response.data.data;
}

/**
 * SYNC NOW — pull the last three days of Meta or Google data now rather than at the next
 * five-hourly sync. The API queues it and reports progress; see `apps/api`'s
 * sync-now.service.ts for the rules (owner/admin only, one at a time, 15-minute cooldown).
 */
export type SyncNowState = "idle" | "queued" | "running" | "completed" | "failed";
export type SyncNowBlock = "not_connected" | "not_allowed" | "in_progress" | "cooldown";

export interface PlatformSyncStatus {
  readonly platform: Platform;
  readonly state: SyncNowState;
  readonly progress: number;
  readonly message: string | null;
  readonly error: string | null;
  readonly lastSyncedAt: string | null;
  readonly canSync: boolean;
  readonly blockedBy: SyncNowBlock | null;
  readonly availableAt: string | null;
}

/** The ticket's wording, verbatim. */
export const SYNC_NOW_CONFIRM_TEXT = "This will start a real time data sync and may take some time. Enter to continue";

export async function fetchSyncStatus(client: AxiosInstance): Promise<readonly PlatformSyncStatus[]> {
  const response = await client.get<{ success: boolean; data: readonly PlatformSyncStatus[] }>("/api/v2/integrations/sync-status");
  return response.data.data;
}

export async function startSync(client: AxiosInstance, platform: Platform): Promise<void> {
  await client.post(`/api/v2/integrations/${platform}/sync`);
}

/** A sync is in flight — the screen polls while this is true. */
export const syncInFlight = (status: PlatformSyncStatus): boolean => status.state === "queued" || status.state === "running";

const ago = (iso: string, now: Date): string => {
  const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 48 ? `${String(hours)} h ago` : `${String(Math.floor(hours / 24))} d ago`;
};

/** The one-line state under the platform's name. */
export function syncStatusText(status: PlatformSyncStatus, now: Date): string {
  const last = status.lastSyncedAt === null ? "Not synced yet" : `Last synced ${ago(status.lastSyncedAt, now)}`;
  switch (status.state) {
    case "queued":
      return "Sync queued — starting shortly…";
    case "running":
      return `Syncing… ${String(status.progress)}%`;
    case "failed":
      return `Last sync failed: ${status.error ?? "unknown error"}. ${last}.`;
    case "completed":
    case "idle":
      return last;
  }
}

/** Why the button is off — shown beside it, so a disabled button never goes unexplained. */
export function syncBlockedReason(status: PlatformSyncStatus, now: Date): string | null {
  switch (status.blockedBy) {
    case null:
      return null;
    case "not_connected":
      return "Select at least one ad account to sync.";
    case "not_allowed":
      return "Only the store owner or an admin can start a sync.";
    case "in_progress":
      return "A sync is already running.";
    case "cooldown": {
      const minutes = status.availableAt === null ? 0 : Math.ceil((new Date(status.availableAt).getTime() - now.getTime()) / 60_000);
      return minutes > 0 ? `Synced recently — available again in ${String(minutes)} min.` : "Synced recently — available again shortly.";
    }
  }
}
